import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { getSupabaseClient } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';
import './Login.css';

/**
 * Login page component located at src/pages/Login/Login.jsx.
 * Accessible when visiting /login URL.
 * Retrieves Supabase keys from AWS SSM / server.
 */
export default function Login({ title = 'Sign in to continue to', brandName = 'Codeazy' }) {
  const [displayText, setDisplayText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const text = 'Learn Tech The Easy Way.';

  // Typewriter effect animation for the brand tagline
  useEffect(() => {
    let index = 0;
    const typingInterval = setInterval(() => {
      setDisplayText(text.slice(0, index + 1));
      index++;
      if (index >= text.length) clearInterval(typingInterval);
    }, 180);

    return () => clearInterval(typingInterval);
  }, []);

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const client = await getSupabaseClient();
      const { error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: { prompt: 'select_account' },
        },
      });

      if (error) {
        console.error('Google login failed:', error.message);
        setErrorMessage(error.message);
      }
    } catch (err) {
      console.error('Login error:', err);
      setErrorMessage(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Already signed in: nothing to do here
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="codeazy-login">
      {/* Ambient background particles and circuit accents */}
      <div className="codeazy-login__background" aria-hidden="true">
        <span className="circuit circuit--one"></span>
        <span className="circuit circuit--two"></span>
        <span className="circuit circuit--three"></span>
        <span className="glow glow--cyan"></span>
        <span className="glow glow--purple"></span>
        <span className="light light--one"></span>
        <span className="light light--two"></span>
        <span className="light light--three"></span>
      </div>

      <div className="codeazy-login__content">
        {/* Brand logo and animated tagline */}
        <div className="codeazy-login__brand">
          <Link to="/" className="codeazy-logo-wrapper" title="Back to Codeazy Home">
            <img src="/codeazy-logo.png" alt={brandName} className="codeazy-logo" />
          </Link>

          <p className="codeazy-login__tagline">
            {displayText}
            <span className="typing-cursor">|</span>
          </p>
        </div>

        <div className="codeazy-login__divider" aria-hidden="true"></div>

        {/* Login action card */}
        <div className="codeazy-login__panel">
          <div className="codeazy-login__card">
            <div className="card-light"></div>

            <h2>
              {title} <strong>{brandName}</strong>
            </h2>

            {errorMessage && (
              <div className="codeazy-login__error" role="alert">
                {errorMessage}
              </div>
            )}

            {/* Google OAuth trigger button */}
            <button
              type="button"
              className="codeazy-login__google"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <span className="google-icon" aria-hidden="true">
                <span className="google-g">G</span>
              </span>
              <span>{loading ? 'Connecting...' : 'Continue with Google'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Login as CodeazyLogin };
